'use client';

import { useState } from 'react';
import { useTranslation } from 'next-i18next';
import { Button } from '@components/common';
import { useSwrRecruiterCallHistory } from '@hooks/useSwrApi/endpoints/recruiter-call';
import type { RecruiterCallHistoryItem } from '@type/recruiter-call';
import { formatBucharestDateTime } from './formatBucharestDateTime';
import { RecruiterCallTranscriptModal } from './RecruiterCallTranscriptModal';

interface RecruiterCallHistorySectionProps {
  candidateUserSlug: string;
  jobSlug?: string | null;
  className?: string;
}

/**
 * CV-detail section. Lists RecruiterCall rows between the logged-in
 * recruiter's account and this candidate, sorted most-recent-first (or
 * job-context first when `jobSlug` is provided). Rows expose a "View
 * transcript" action that opens the transcript+AI modal inline.
 *
 * The list is empty when the recruiter has no account (backend returns
 * `{items: []}`) or when this candidate has never been called from any
 * user on this account.
 */
export const RecruiterCallHistorySection = ({
  candidateUserSlug,
  jobSlug,
  className,
}: RecruiterCallHistorySectionProps) => {
  const { t } = useTranslation('common');
  const [selectedCall, setSelectedCall] = useState<RecruiterCallHistoryItem | null>(null);

  const { data, isLoading } = useSwrRecruiterCallHistory({
    candidateUserSlug,
    jobSlug,
  });

  const items = data?.items ?? [];

  if (isLoading) {
    return (
      <section className={className}>
        <h3 className="mb-3 text-lg font-bold">
          {t('cv.detail.calls.section.title', { defaultValue: 'Istoric apeluri' })}
        </h3>
        <p className="text-sm text-ink-medium">
          {t('global.label.loading', { defaultValue: 'Se încarcă…' })}
        </p>
      </section>
    );
  }

  if (items.length === 0) {
    return null; // hide the section entirely when there's nothing to show
  }

  return (
    <section className={className}>
      <h3 className="mb-3 text-lg font-bold">
        {t('cv.detail.calls.section.title', { defaultValue: 'Istoric apeluri' })}
      </h3>

      <ul className="divide-y divide-input/40 rounded-2xl border border-input/40 bg-surface">
        {items.map((call) => (
          <li key={call.id} className="flex flex-wrap items-center gap-3 p-3 md:flex-nowrap">
            <div className="min-w-40 shrink-0 text-sm">
              <div className="font-semibold text-ink">
                {formatBucharestDateTime(call.startedAt ?? call.createdAt ?? null)}
              </div>
              {call.durationSeconds !== undefined && call.durationSeconds !== null && (
                <div className="text-xs text-ink-medium">
                  {Math.floor((call.durationSeconds ?? 0) / 60).toString().padStart(2, '0')}
                  :
                  {((call.durationSeconds ?? 0) % 60).toString().padStart(2, '0')}
                </div>
              )}
            </div>

            <div className="min-w-32 shrink-0 text-sm">
              <StatusPill call={call} />
            </div>

            <div className="ml-auto shrink-0">
              <Button
                type="button"
                styling="text"
                color="secondary"
                onClick={() => setSelectedCall(call)}
                disabled={call.processingStatus !== 'ready'}
                className="text-sm font-semibold"
              >
                {t('cv.detail.calls.view_transcript', { defaultValue: 'Vezi transcript' })}
              </Button>
            </div>
          </li>
        ))}
      </ul>

      <RecruiterCallTranscriptModal
        open={selectedCall !== null}
        call={selectedCall}
        onClose={() => setSelectedCall(null)}
      />
    </section>
  );
};

/**
 * Single consolidated status the recruiter cares about. Prefers the
 * processing state (transcript ready / transcribing / pending) because
 * that's what determines what they can actually DO with the row —
 * the Twilio lifecycle state is only useful for failure diagnosis and
 * gets rolled up into a single "Fără transcript" (unavailable) label.
 */
const StatusPill = ({ call }: { call: RecruiterCallHistoryItem }) => {
  const { t } = useTranslation('common');
  const processing = call.processingStatus ?? 'pending';
  const isRinging = call.status === 'ringing' || call.status === 'in-progress' || call.status === 'initiated';

  // If we're mid-call, the processing state is meaningless — show call state.
  if (isRinging && processing === 'pending') {
    return (
      <span className="inline-block rounded-full bg-warning/10 px-2 py-0.5 text-xs font-medium text-warning">
        {t('cv.detail.calls.status.in_progress', { defaultValue: 'În curs' })}
      </span>
    );
  }

  const map: Record<string, { labelKey: string; defaultLabel: string; className: string }> = {
    ready: {
      labelKey: 'cv.detail.calls.status.transcript_ready',
      defaultLabel: 'Transcript pregătit',
      className: 'bg-secondary/10 text-secondary',
    },
    transcribing: {
      labelKey: 'cv.detail.calls.status.processing',
      defaultLabel: 'Se procesează',
      className: 'bg-primary-light/10 text-primary-light',
    },
    pending: {
      labelKey: 'cv.detail.calls.status.pending',
      defaultLabel: 'În așteptare',
      className: 'bg-warning/10 text-warning',
    },
    unavailable: {
      labelKey: 'cv.detail.calls.status.unavailable',
      defaultLabel: 'Fără transcript',
      className: 'bg-ink-medium/10 text-ink-medium',
    },
  };
  const config = map[processing] ?? map.pending;

  return (
    <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${config.className}`}>
      {t(config.labelKey, { defaultValue: config.defaultLabel })}
    </span>
  );
};
